import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Route } from 'react-router-dom';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import { getClassName } from 'utils/ui/classes';
import { titleize } from 'underscore.string';
import classNames from 'classnames';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import UserActionCreators from '../../actions/UserActionCreators';
import CustomerSearch from './customer-search';
import ShipmentSearch from './shipment-search';
import SignedUrl from '../../utils/SignedUrlUtils';
import CargomaticPane from '../v2/pane';
import PlusIcon from '../v2/icons/PlusIcon';
import Navbar from '../v2/navs/Navbar';
import CollapsibleMenu from '../v2/navs/CollapsibleMenu';
import { isProd, isApacProd } from '../../helpers/helper_functions';
import LanguageSelector from './language-selector';

const activeStyle = {
  fontWeight: 'bold',
};

const openReportsIndexPage = async () => {
  try {
    const url = await SignedUrl.getSignedUrl('reporting', 'index.html');
    window.open(url, 'new');
  } catch (e) {
    console.error('nav signed url: ', e);
  }
};

function logout() {
  UserActionCreators.logout();
}

const userDoesNotHaveShipperOrCarrierRole = () => {
  return !SessionStore.isShipper() && !SessionStore.isCarrier();
};

const hasAdminRole = () => {
  return SessionStore.getUserRoles().indexOf('admin') > -1;
};

const baseClassName =
  'group flex items-center pl-10 pr-2 py-2 text-sm font-medium rounded-md cursor-pointer';

const Logo = (
  <NavLink to="/fleet">
    <img alt="Cargomatic" src={CargomaticLogo} />
  </NavLink>
);

export default function SiteNav() {
  const [showNav, setShowNav] = useState(false);
  const { t } = useTranslation();

  const handleToggleMenu = () => {
    setShowNav(!showNav);
  };

  function hideNav() {
    setShowNav(false);
  }

  function renderNavItems() {
    return (
      <div data-testid="navArea" className="px-2 py-4 overflow-y-auto">
        <CollapsibleMenu title={t('SHIPMENTS_ORDERS')}>
          {!isApacProd() && (
            <NavLink
              data-testid="navCreateShipment"
              activeStyle={activeStyle}
              key="booking"
              to="/booking"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('CREATE_SHIPMENT')}</span>
            </NavLink>
          )}
          <a
            data-testid="navCreateOrder"
            key="order-booking"
            href="/order-booking"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('CREATE_ORDER')}</span>
          </a>
          {!isApacProd() && (
            <NavLink
              data-testid="navViewAndCreateQuotes"
              activeStyle={activeStyle}
              key="quotes"
              to="/quotes"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('VIEW_AND_CREATE_QUOTES')}</span>
            </NavLink>
          )}
          <NavLink
            data-testid="navViewShipments"
            activeStyle={activeStyle}
            key="shipments"
            to="/shipments"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('VIEW_SHIPMENTS')}</span>
          </NavLink>
          <NavLink
            data-testid="navViewOrders"
            activeStyle={activeStyle}
            key="orders"
            to="/orders"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('VIEW_ORDERS')}</span>
          </NavLink>
          <NavLink
            data-testid="navViewOffers"
            activeStyle={activeStyle}
            key="loadboard"
            to="/loadboard"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
              'admin-menu offers-offerlist heap',
            ])}
          >
            <span>{t('VIEW_OFFERS')}</span>
          </NavLink>
          {!isProd() && (
            <NavLink
              data-testid="navOfferPlanner"
              activeStyle={activeStyle}
              key="offer-planner"
              to="/offer-planner"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
                'admin-menu offers-offerlist heap',
              ])}
            >
              <span>{t('OFFER_PLANNER')}</span>
            </NavLink>
          )}
          {userDoesNotHaveShipperOrCarrierRole() && (
            <div>
              <NavLink
                data-testid="navUnifiedView"
                activeStyle={activeStyle}
                key="unifiedview"
                to="/unifiedview/favorites"
                onClick={hideNav}
                className={getClassName(baseClassName, [
                  'text-gray-600',
                  'hover:bg-gray-50',
                ])}
              >
                <span>{t('UNIFIED_VIEW')}</span>
              </NavLink>
            </div>
          )}
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            {!isProd() && (
              <NavLink
                data-testid="navBulkShipmentCreation"
                activeStyle={activeStyle}
                key="bulkshipmentcreation"
                to="/bulk"
                onClick={hideNav}
                className={getClassName(baseClassName, [
                  'text-gray-600',
                  'hover:bg-gray-50',
                ])}
              >
                <span>{t('BULK_SHIPMENT_CREATION')}</span>
              </NavLink>
            )}
          </AuthorizationWrapper>
        </CollapsibleMenu>
        <CollapsibleMenu title={t('EXCEPTIONS')}>
          <NavLink
            data-testid="navExceptionAccessorials"
            activeStyle={activeStyle}
            key="exceptions"
            to="/exceptions/accessorials"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('ACCESSORIAL', { count: 2 })}</span>
          </NavLink>
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            <NavLink
              data-testid="navOrders"
              activeStyle={activeStyle}
              key="order-exceptions"
              to="/exceptions/orders"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{titleize(t('ORDER', { count: 2 }))}</span>
            </NavLink>
          </AuthorizationWrapper>
          <NavLink
            data-testid="navShipments"
            activeStyle={activeStyle}
            key="shipment-exceptions"
            to="/exceptions/shipments"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('SHIPMENT', { count: 2 })}</span>
          </NavLink>
          <NavLink
            data-testid="navVCarrierDashboard"
            activeStyle={activeStyle}
            key="carrier-dashboard"
            to="/carriers/dashboard"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('CARRIER_DASHBOARD_TITLE')}</span>
          </NavLink>
        </CollapsibleMenu>
        <CollapsibleMenu title={t('MANAGE')}>
          {SessionStore.isAdmin() && (
            <NavLink
              data-testid="navManageAccessorial"
              activeStyle={activeStyle}
              key="accessorial-tools"
              to="/tools/accessorials"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('ACCESSORIAL', { count: 2 })}</span>
            </NavLink>
          )}
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            <NavLink
              data-testid="navAppointments"
              activeStyle={activeStyle}
              key="appointments"
              to="/appointments"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('APPOINTMENTS')}</span>
            </NavLink>
          </AuthorizationWrapper>
          <NavLink
            data-testid="navCustomers"
            activeStyle={activeStyle}
            key="customers"
            to="/customers"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('CUSTOMERS')}</span>
          </NavLink>
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            <NavLink
              data-testid="navEmptyReturnCustomers"
              activeStyle={activeStyle}
              key="empty-return-instructions"
              to="/empty-returns/instructions"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('EMPTY_RETURN_INSTRUCTIONS')}</span>
            </NavLink>
          </AuthorizationWrapper>
          <NavLink
            data-testid="navEquipment"
            activeStyle={activeStyle}
            key="equipment"
            to="/equipment"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('EQUIPMENT')}</span>
          </NavLink>
          <NavLink
            data-testid="navLocations"
            activeStyle={activeStyle}
            key="locations"
            to="/locations"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('LOCATIONS')}</span>
          </NavLink>
          <NavLink
            data-testid="navWarehouse"
            activeStyle={activeStyle}
            key="warehouse"
            to="/manage/warehouse"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('WAREHOUSE')}</span>
          </NavLink>
          {hasAdminRole() && (
            <NavLink
              data-testid="navMarkets"
              activeStyle={activeStyle}
              key="markets"
              to="/markets"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{titleize(t('MARKET', { count: 2 }))}</span>
            </NavLink>
          )}
          <NavLink
            data-testid="navRegisterUsers"
            activeStyle={activeStyle}
            key="users"
            to="/users"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('REGISTER_USERS')}</span>
          </NavLink>
          <NavLink
            data-testid="navTariffs"
            activeStyle={activeStyle}
            key="tariffs"
            to="/tools/tariffs"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('TARIFFS')}</span>
          </NavLink>

          <NavLink
            data-testid="navTools"
            activeStyle={activeStyle}
            key="tools"
            to="/tools"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            {t('TOOLS')}
          </NavLink>
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            {SessionStore.isACIAdmin() && (
              <NavLink
                data-testid="navZones"
                activeStyle={activeStyle}
                key="zones"
                to="/tools/zones"
                onClick={hideNav}
                className={getClassName(baseClassName, [
                  'text-gray-600',
                  'hover:bg-gray-50',
                ])}
              >
                <span>{t('ZONES')}</span>
              </NavLink>
            )}
          </AuthorizationWrapper>
          <AuthorizationWrapper>
            {SessionStore.userAllowedToManageReferenceNumbersMasterList() && (
              <NavLink
                data-testid="navReferenceNumbers"
                activeStyle={activeStyle}
                key="referenceNumbers"
                to="/tools/reference-numbers"
                onClick={hideNav}
                className={getClassName(baseClassName, [
                  'text-gray-600',
                  'hover:bg-gray-50',
                ])}
              >
                <span>{t('REFERENCE_NUMBERS')}</span>
              </NavLink>
            )}
          </AuthorizationWrapper>

          {hasAdminRole() && (
            <NavLink
              activeStyle={activeStyle}
              key="yards"
              to="/yards"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{titleize(t('YARD_other', { count: 2 }))}</span>
            </NavLink>
          )}
        </CollapsibleMenu>
        <CollapsibleMenu title={t('PRICING')}>
          <AuthorizationWrapper excludedRegions={REGIONS.AP}>
            <NavLink
              data-testid="quickPay"
              activeStyle={activeStyle}
              key="quick_pay"
              to="/pricing/quick-pay"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('QUICK_PAY')}</span>
            </NavLink>
          </AuthorizationWrapper>
          {SessionStore.isAdmin() && (
            <NavLink
              data-testid="navLaneRates"
              activeStyle={activeStyle}
              key="lane-rates"
              to="/lane-rates"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('LANE_RATES')}</span>
            </NavLink>
          )}
          {SessionStore.isAdmin() && (
            <NavLink
              data-testid="navAccessorialRates"
              activeStyle={activeStyle}
              key="accessorial-rates"
              to="/accessorial-rates"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('ACCESSORIAL_RATES')}</span>
            </NavLink>
          )}
        </CollapsibleMenu>
        <CollapsibleMenu title={t('ACCOUNTING')}>
          <NavLink
            data-testid="navInvoices"
            activeStyle={activeStyle}
            to="/invoices"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('INVOICE', { count: 2 })}</span>
          </NavLink>
          <NavLink
            data-testid="navPayouts"
            activeStyle={activeStyle}
            key="payouts"
            to="/payouts"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>{t('PAYOUT', { count: 2 })}</span>
          </NavLink>
          {SessionStore.isAdmin() && (
            <NavLink
              data-testid="navCarrierCostBulkAuditTool"
              key="carrier-bulk-audit-tool"
              to="/carriers/bulk-audit-tool"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('CARRIER_COST_BULK_AUDIT_TOOL')}</span>
            </NavLink>
          )}
        </CollapsibleMenu>
        <AuthorizationWrapper excludedRegions={REGIONS.AP}>
          <CollapsibleMenu title={t('REPORTING')}>
            <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
              <a
                data-testid="navCustomReports"
                href="#"
                key="reporting"
                className={getClassName(baseClassName, [
                  'text-gray-600',
                  'hover:bg-gray-50',
                ])}
                onClick={(e) => {
                  e.preventDefault();
                  hideNav();
                  openReportsIndexPage();
                }}
              >
                {t('CUSTOM_REPORTS')}
              </a>
            </AuthorizationWrapper>
            <NavLink
              data-testid="navDaily"
              activeStyle={activeStyle}
              to="/reporting/daily"
              key="daily-reporting"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{titleize(t('DAILY'))}</span>
            </NavLink>
            <NavLink
              data-testid="navGrowth"
              activeStyle={activeStyle}
              to="/reporting/growth"
              key="growth-reporting"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('GROWTH')}</span>
            </NavLink>
            {/* Hiding Cargomatic Scorecard until data is tested... */}
            {/* <NavLink
            to="/metrics"
            key="cargomatic-metrics"
            onClick={hideNav}
            className={getClassName(baseClassName, [
              'text-gray-600',
              'hover:bg-gray-50',
            ])}
          >
            <span>Cargomatic Scorecard</span>
          </NavLink> */}
          </CollapsibleMenu>
        </AuthorizationWrapper>
        {hasAdminRole() && (
          <CollapsibleMenu title={t('MARKETPLACE_CONFIG')}>
            <NavLink
              data-testid="navMarkup"
              activeStyle={activeStyle}
              to="/marketplace-config/markup"
              key="markup-config"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('MARKUP')}</span>
            </NavLink>
            <NavLink
              data-testid="navReasonCodes"
              activeStyle={activeStyle}
              to="/marketplace-config/reason_codes"
              key="reason-codes"
              onClick={hideNav}
              className={getClassName(baseClassName, [
                'text-gray-600',
                'hover:bg-gray-50',
              ])}
            >
              <span>{t('REASON_CODES')}</span>
            </NavLink>
          </CollapsibleMenu>
        )}
      </div>
    );
  }

  function renderRightNav() {
    const items = [];
    items.push(
      <div className="flex items-center space-x-2" key="search">
        <div className="w-44 xl:w-60">
          <ShipmentSearch />
        </div>
        <div className="w-44 xl:w-60">
          <Route
            render={() => (
              <CustomerSearch
                id="customer-search-admin"
                onSelect={(_name, details) => {
                  window.location.href = `/users/${details.value}`;
                }}
                menuStyle={{
                  position: 'fixed',
                  overflow: 'hidden',
                  maxHeight: '50vh',
                  zIndex: 100,
                  cursor: 'pointer',
                  overflowY: 'scroll',
                }}
              />
            )}
          />
        </div>
      </div>,
    );

    items.push(
      <div
        className={classNames('flex items-center justify-center nav-item', {
          hidden: LANGUAGE_SELECTION_DISABLED,
        })}
      >
        <LanguageSelector />
      </div>,
    );

    items.push(
      <a key="signout" href="#" className="nav-item" onClick={logout}>
        {t('SIGN_OUT')}
      </a>,
    );
    return items;
  }

  function renderMobileNav(onClose) {
    const items = [];
    items.push(
      <div className="pl-2 flex flex-col space-y-2 justify-center items-start w-full">
        <div className="w-full">
          <ShipmentSearch
            closeOnSelect
            onClose={onClose}
            menuStyle={{
              position: 'fixed',
              overflow: 'hidden',
              maxHeight: '50vh',
              zIndex: 100,
              cursor: 'pointer',
              overflowY: 'scroll',
            }}
          />
        </div>
        <div className="w-full mb-2">
          <Route
            render={({ history }) => (
              <CustomerSearch
                id="customer-search-admin"
                onSelect={(_name, details) => {
                  history.push('/users/' + details.value);
                  onClose();
                }}
              />
            )}
          />
        </div>
      </div>,
    );
    items.push(
      <NavLink
        activeStyle={activeStyle}
        key="mobile-booking"
        to="/booking"
        className="nav-item"
        onClick={onClose}
      >
        <span>{t('CREATE_SHIPMENT')}</span>
      </NavLink>,
    );
    items.push(
      <NavLink
        activeStyle={activeStyle}
        key="mobile-quotes"
        to="/quotes"
        className="nav-item"
        onClick={onClose}
      >
        <span>{t('VIEW_AND_CREATE_QUOTES')}</span>
      </NavLink>,
    );
    items.push(
      <NavLink
        activeStyle={activeStyle}
        key="mobile-shipments"
        to="/shipments"
        className="nav-item"
        onClick={onClose}
      >
        <span>{t('VIEW_SHIPMENTS')}</span>
      </NavLink>,
    );
    items.push(
      <NavLink
        activeStyle={activeStyle}
        key="loadboard"
        to="/loadboard"
        className="nav-item admin-mobile-menu offers-offerlist heap"
        onClick={onClose}
      >
        <span>{t('VIEW_OFFERS')}</span>
      </NavLink>,
    );
    items.push(
      <a
        key="mobile-order-booking"
        href="/order-booking"
        className="nav-item"
        onClick={onClose}
      >
        <span>{t('CREATE_ORDER')}</span>
      </a>,
      <NavLink
        activeStyle={activeStyle}
        key="mobile-orders"
        to="/orders"
        className="nav-item"
        onClick={onClose}
      >
        <span>{t('VIEW_ORDERS')}</span>
      </NavLink>,
    );
    items.push(
      <div
        className={classNames('nav-item', {
          hidden: LANGUAGE_SELECTION_DISABLED,
        })}
      >
        <LanguageSelector mobile />
      </div>,
    );
    items.push(
      <a
        key="mobile-signout"
        href="#"
        className="nav-item"
        onClick={(e) => {
          e.preventDefault();
          logout();
        }}
      >
        {t('SIGN_OUT')}
      </a>,
    );
    return items;
  }
  function renderSidebar() {
    return (
      <CargomaticPane
        show={showNav}
        onHide={hideNav}
        title="Navigation"
        location="left"
        renderBody={renderNavItems}
        width={0.25}
      />
    );
  }

  return (
    <div>
      <div>
        <div id="pane-container">{renderSidebar()}</div>
        <Navbar
          logo={Logo}
          showLeftMenuToggle
          renderRightNav={renderRightNav}
          renderMobileNav={renderMobileNav}
          mainNavOpen={showNav}
          onToggleOpen={handleToggleMenu}
        >
          {!isApacProd() && (
            <NavLink
              activeStyle={activeStyle}
              key="booking"
              to="/booking"
              className="flex-grow-0 flex-shrink-0 cursor button button-green hover:text-white"
            >
              <div className="flex items-center space-x-2">
                <PlusIcon />
                <span className="">{t('SHIPMENT', { count: 1 })}</span>
              </div>
            </NavLink>
          )}
          <a
            key="order-booking"
            href="/order-booking"
            className="flex-grow-0 flex-shrink-0 nav-item cursor button button-blue"
          >
            <div className="flex items-center space-x-2">
              <PlusIcon />
              <span className="">{titleize(t('ORDER', { count: 1 }))}</span>
            </div>
          </a>
          <NavLink
            activeStyle={activeStyle}
            key="shipments"
            to="/shipments"
            className="hidden nav-item xl:block"
          >
            <span>{t('SHIPMENT', { count: 2 })}</span>
          </NavLink>
        </Navbar>
      </div>
    </div>
  );
}
